import React, { useEffect, useState } from 'react';
import { Box, Input, Select, MenuItem } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  CountryCode, StateCode, StreetAddress, UnitType,
} from 'API';
import { DICTIONARY, FORM_MAX_LENGTH } from 'shared/constants';
import { resolvers } from 'shared/enums';
import slugify from 'slugify';
import states from 'shared/states.json';
import GeoPicker from './GeoPicker';

const unitResolver = resolvers.UnitType;

const fieldsetSx = {
  flexDirection: 'column',
  margin: '0.5rem 0',
  '& > *': { display: 'block' },
};

interface Props {
  className?: string;
  isUS?: boolean;
  name: string;
  onChange: (name: string, address: StreetAddress) => void;
  useCareOf?: boolean;
  value: StreetAddress | null;
}

const StreetAddressEditor: React.FC<Props> = ({
  className,
  name,
  onChange,
  isUS,
  useCareOf,
  value,
}: Props) => {
  const [address, setAddress] = useState<StreetAddress>(
    value || {
      country: isUS ? CountryCode.US : null,
    } as StreetAddress,
  );
  const slug = slugify(name);

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    // Keep state and adminArea in sync
    let newStateCode = null;

    if (
      e.target.name === 'adminArea'
      && states.find((state) => state.state === e.target.value)) {
      newStateCode = states.find(
        (state) => state.state === e.target.value,
      )?.code as StateCode;
    }

    setAddress({
      ...address,
      ...(e.target.name === 'adminArea' ? { state: newStateCode } : {}),
      [e.target.name]: e.target.value,
    });
  };

  const countryHandler = (event: any, country: CountryCode | null) => {
    let { state } = address;
    if (country !== CountryCode.US) {
      state = null;
    }
    setAddress({
      ...address,
      state,
      country,
    });
  };

  const stateHandler = (event: any, state: StateCode | null) => {
    // Keep state and adminArea in sync
    const newAdminArea = states.find((s) => s.code === state)?.state || null;

    setAddress({
      ...address,
      state,
      adminArea: newAdminArea,
    });
  };

  const unitTypeHandler = (event: SelectChangeEvent<any>) => {
    const eventValue = event.target.value;
    setAddress({
      ...address,
      unitType: eventValue === UnitType.NONE ? null : eventValue as UnitType | null,
    });
  };

  const unitTypes = Object.keys(UnitType);

  useEffect(() => {
    onChange(name, address);
  }, [address]);

  return (
    <Box
      className={className}
      sx={{ maxWidth: '100%', '& > :last-child': { marginBottom: 0 } }}
    >
      {!isUS && (
        <Box sx={fieldsetSx}>
          <label htmlFor={`field-${slug}-adminArea`} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
            {DICTIONARY.Country}
          </label>
          <GeoPicker
            type="country"
            onChange={countryHandler}
            placeholder="Country"
            value={address.country || null}
          />
        </Box>
      )}
      {useCareOf && (
        <Box sx={fieldsetSx}>
          <label htmlFor={`field-${slug}-address`} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
            {DICTIONARY.CareOf}
          </label>
          <Input
            id={`field-${slug}-careOf`}
            name="careOf"
            type="text"
            onChange={changeHandler}
            placeholder="c/o"
            value={address.careOf || ''}
            inputProps={{ maxLength: FORM_MAX_LENGTH }}
          />
        </Box>
      )}
      <Box sx={fieldsetSx}>
        <label htmlFor={`field-${slug}-address`} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
          {DICTIONARY.Address}
        </label>
        <Input
          id={`field-${slug}-address`}
          multiline
          fullWidth
          name="address"
          type="text"
          onChange={changeHandler}
          placeholder="123 Main St."
          value={address.address || ''}
          inputProps={{ maxLength: FORM_MAX_LENGTH }}
        />
      </Box>

      <Box sx={(t) => ({ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1rem', '& > *': { width: '100%' }, [t.breakpoints.down('lg')]: { gridTemplateColumns: '1fr' } })}>
        <Box sx={fieldsetSx}>
          <label htmlFor={`field-${slug}-unitType`} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
            {DICTIONARY.UnitType}
          </label>
          <Select
            id={`field-${slug}-unitType`}
            name="unitType"
            onChange={unitTypeHandler}
            value={address.unitType || ''}
            style={{ width: '100%' }}
          >
            {unitTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {unitResolver(type as UnitType)}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={fieldsetSx}>
          <label htmlFor={`field-${slug}-unitNumber`} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
            {DICTIONARY.UnitNumber}
            <Input
              id={`field-${slug}-unitNumber`}
              name="unitNumber"
              type="text"
              onChange={changeHandler}
              style={{ width: '100%' }}
              value={address.unitNumber || ''}
              inputProps={{ maxLength: FORM_MAX_LENGTH }}
            />
          </label>
        </Box>
      </Box>

      <Box sx={fieldsetSx}>
        <label htmlFor={`field-${slug}-city`} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
          {DICTIONARY.City}
        </label>
        <Input
          id={`field-${slug}-city`}
          name="city"
          type="text"
          onChange={changeHandler}
          placeholder="Chicago"
          value={address.city || ''}
          inputProps={{ maxLength: FORM_MAX_LENGTH }}
        />
      </Box>
      <Box sx={(t) => ({ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1rem', '& > *': { width: '100%' }, [t.breakpoints.down('lg')]: { gridTemplateColumns: '1fr' } })}>
        <Box sx={fieldsetSx}>
          <label htmlFor={`field-${slug}-adminArea`} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
            {!address.country || (address.country === CountryCode.US)
              ? DICTIONARY.State
              : DICTIONARY.StateProvince}
          </label>
          {!address.country || address.country === CountryCode.US ? (
            <GeoPicker
              type="state"
              onChange={stateHandler}
              placeholder="Illinois"
              value={address.state || null}
            />
          ) : (
            <Input
              id={`field-${slug}-stateProvince`}
              name="adminArea"
              type="text"
              onChange={changeHandler}
              placeholder="Province"
              value={address.adminArea || ''}
              inputProps={{ maxLength: FORM_MAX_LENGTH }}
            />
          )}
        </Box>

        <Box sx={fieldsetSx}>
          <label htmlFor={`field-${slug}-postalCode`} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
            {!address.country || (address.country === CountryCode.US)
              ? DICTIONARY.Zip
              : DICTIONARY.PostalCode}
          </label>
          <Input
            id={`field-${slug}-postalCode`}
            name="postalCode"
            type="text"
            onChange={changeHandler}
            placeholder="60602"
            value={address.postalCode || ''}
            inputProps={{ maxLength: FORM_MAX_LENGTH }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default StreetAddressEditor;
